import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@mui/material";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditFaqDialog from "../../components/product-manager/EditFaqDialog";
import BulkEntityUploadDialog from "../../components/product-manager/BulkEntityUploadDialog";
import { FAQ_BULK_CONFIG } from "../../utils/bulkUpload/bulkEntityConfigs";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useFaqs } from "../../hooks/useFaqs";
import { apiService } from "../../api/services/apiService";
import type { FAQ } from "../../types";
import { EntityStatus } from "../../types";
import { pmHubAddPath } from "../../utils/pmHubRoutes";

const FaqHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { faqs: fetched, loading, error, refresh } = useFaqs();
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setFaqs(fetched);
  }, [fetched]);

  const filtered = useMemo(
    () =>
      filterByHubState(faqs, hubState, undefined, (f) => [
        f.name,
        f.description ?? "",
        f.question ?? "",
        f.answer ?? "",
      ]),
    [faqs, hubState],
  );

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const editFaq = useMemo(
    () => faqs.find((f) => f.uuid === hubState.editUuid) ?? null,
    [faqs, hubState.editUuid],
  );

  const handleToggle = async (faq: FAQ) => {
    setToggleLoadingId(faq.uuid);
    const newStatus =
      faq.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateFaq(faq.uuid, {
        name: faq.name,
        description: faq.description ?? undefined,
        question: faq.question ?? undefined,
        answer: faq.answer ?? undefined,
        status: newStatus,
      });
      setFaqs((prev) =>
        prev.map((f) => (f.uuid === updated.uuid ? updated : f)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="FAQ Hub"
        hubDescription="Manage FAQs with individual and bulk create, update, and enable or disable."
        addPath={pmHubAddPath("faq")}
        addLabel="Add FAQ"
        onBulkUpload={() => setBulkOpen(true)}
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
      >
        <Grid container spacing={2}>
          {paginated.map((faq) => (
            <Grid item xs={12} sm={6} md={4} key={faq.uuid}>
              <EntityAssetCard
                title={faq.name}
                subtitle={faq.question ?? undefined}
                status={faq.status}
                enabled={faq.status === EntityStatus.ACTIVE}
                toggleLoading={toggleLoadingId === faq.uuid}
                onToggleEnabled={() => handleToggle(faq)}
                onEdit={() => hubState.setEditUuid(faq.uuid)}
                onDelete={async () => {
                  await apiService.deleteFaq(faq.uuid);
                  setFaqs((prev) => prev.filter((f) => f.uuid !== faq.uuid));
                }}
              />
            </Grid>
          ))}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No FAQs match your filters.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <BulkEntityUploadDialog
        open={bulkOpen}
        config={FAQ_BULK_CONFIG}
        onClose={() => setBulkOpen(false)}
        onComplete={() => refresh()}
      />

      <EditFaqDialog
        faq={editFaq}
        open={Boolean(hubState.editUuid && editFaq)}
        onClose={() => hubState.setEditUuid(null)}
        onSaved={(updated) =>
          setFaqs((prev) =>
            prev.map((f) => (f.uuid === updated.uuid ? updated : f)),
          )
        }
      />
    </>
  );
};

export default FaqHubPage;
